import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  Shield,
  User,
  MoreVertical,
  Mail,
  Trash2,
  Edit2,
  UserCheck,
  UserX,
  Crown,
} from 'lucide-react';

const TeamMemberCard = ({ member, currentUserId, onEdit, onDelete, isAdmin }) => {
  const isCurrentUser = member.id === currentUserId;
  
  return (
    <Card className="card-hover" data-testid={`team-member-${member.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              {member.role === 'admin' ? (
                <Crown className="w-6 h-6 text-yellow-500" />
              ) : (
                <User className="w-6 h-6 text-primary" />
              )}
            </div>
            
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-medium">{member.full_name}</h3>
                {isCurrentUser && (
                  <Badge variant="outline" className="text-xs">Vous</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" /> {member.email}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge 
              variant="outline"
              className={member.role === 'admin' 
                ? 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30'
                : 'bg-blue-500/10 text-blue-600 border-blue-500/30'
              }
            >
              {member.role === 'admin' ? 'Admin' : 'Staff'}
            </Badge>
            
            <Badge 
              variant="outline"
              className={member.is_active 
                ? 'bg-green-500/10 text-green-600 border-green-500/30'
                : 'bg-red-500/10 text-red-600 border-red-500/30'
              }
            >
              {member.is_active ? 'Actif' : 'Inactif'}
            </Badge>
            
            {isAdmin && !isCurrentUser && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid={`member-menu-${member.id}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(member)}>
                    <Edit2 className="w-4 h-4 mr-2" /> Modifier
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onDelete(member.id)}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PERMISSIONS_CONFIG = [
  { key: 'can_edit_clients', label: 'Modifier les clients', description: 'Créer, modifier, supprimer des clients' },
  { key: 'can_validate_quote', label: 'Valider les devis', description: 'Passer un devis en Envoyé/Signé' },
  { key: 'can_manage_products', label: 'Gérer les produits', description: 'Ajouter, modifier, supprimer des produits' },
  { key: 'can_view_prices', label: 'Voir les prix', description: 'Afficher les prix et totaux' },
  { key: 'can_create_events', label: 'Créer des événements', description: 'Ajouter des événements au planning' },
  { key: 'can_delete_quotes', label: 'Supprimer des devis', description: 'Supprimer définitivement un devis' },
];

const DEFAULT_PERMISSIONS = {
  can_edit_clients: true,
  can_validate_quote: false,
  can_manage_products: true,
  can_view_prices: true,
  can_manage_team: false,
  can_create_events: true,
  can_delete_quotes: false,
};

const InviteDialog = ({ open, onOpenChange, onInvite }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'staff',
    permissions: { ...DEFAULT_PERMISSIONS },
  });

  const handlePermissionChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: value }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onInvite(formData);
    setFormData({ email: '', full_name: '', role: 'staff', permissions: { ...DEFAULT_PERMISSIONS } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Inviter un membre</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Nom complet *</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
              data-testid="invite-name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="invite-email"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Rôle</Label>
            <Select
              value={formData.role}
              onValueChange={(v) => setFormData({ ...formData, role: v })}
            >
              <SelectTrigger data-testid="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            {formData.role === 'admin' && (
              <p className="text-xs text-amber-500">Les admins ont toutes les permissions par défaut</p>
            )}
          </div>
          
          {formData.role === 'staff' && (
            <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
              <Label className="text-sm font-medium">Permissions</Label>
              {PERMISSIONS_CONFIG.map(({ key, label, description }) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    checked={formData.permissions[key] || false}
                    onCheckedChange={(v) => handlePermissionChange(key, v)}
                    data-testid={`invite-perm-${key}`}
                  />
                </div>
              ))}
            </div>
          )}
          
          <p className="text-sm text-muted-foreground">
            Un email avec les identifiants sera envoyé à l'utilisateur.
          </p>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" data-testid="invite-submit">
              Inviter
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditMemberDialog = ({ member, open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState({
    full_name: member?.full_name || '',
    role: member?.role || 'staff',
    is_active: member?.is_active ?? true,
    permissions: member?.permissions || { ...DEFAULT_PERMISSIONS },
  });

  React.useEffect(() => {
    if (member) {
      setFormData({
        full_name: member.full_name,
        role: member.role,
        is_active: member.is_active,
        permissions: member.permissions || { ...DEFAULT_PERMISSIONS },
      });
    }
  }, [member]);

  const handlePermissionChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: value }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(member.id, formData);
  };

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier le membre</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit_name">Nom complet</Label>
            <Input
              id="edit_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              data-testid="edit-member-name"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Rôle</Label>
            <Select
              value={formData.role}
              onValueChange={(v) => setFormData({ ...formData, role: v })}
            >
              <SelectTrigger data-testid="edit-member-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            {formData.role === 'admin' && (
              <p className="text-xs text-amber-500">Les admins ont toutes les permissions</p>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label>Compte actif</Label>
              <p className="text-sm text-muted-foreground">
                Désactiver empêche la connexion
              </p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              data-testid="edit-member-active"
            />
          </div>
          
          {formData.role === 'staff' && (
            <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
              <Label className="text-sm font-medium">Permissions granulaires</Label>
              {PERMISSIONS_CONFIG.map(({ key, label, description }) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    checked={formData.permissions[key] || false}
                    onCheckedChange={(v) => handlePermissionChange(key, v)}
                    data-testid={`edit-perm-${key}`}
                  />
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" data-testid="edit-member-submit">
              Enregistrer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TeamPage = () => {
  const { token, user } = useAuthStore();
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  
  const isAdmin = user?.role === 'admin';

  const { data: team = [], isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Admin access required');
        }
        throw new Error('Failed to fetch team');
      }
      return res.json();
    },
    enabled: isAdmin,
  });

  const inviteMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to invite');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      toast.success('Invitation envoyée (vérifiez les logs serveur pour le mot de passe)');
      setInviteOpen(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/team/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      toast.success('Membre mis à jour');
      setEditingMember(null);
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/team/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      toast.success('Membre supprimé');
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });

  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="team-page-restricted">
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">Accès restreint</h3>
            <p className="text-muted-foreground">
              Seuls les administrateurs peuvent gérer l'équipe.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Équipe</h1>
          <p className="text-muted-foreground">Gérez les membres de votre équipe</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} data-testid="invite-member-btn">
          <Plus className="w-4 h-4 mr-2" /> Inviter un membre
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{team.length}</p>
            <p className="text-sm text-muted-foreground">Total membres</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-500">
              {team.filter(m => m.role === 'admin').length}
            </p>
            <p className="text-sm text-muted-foreground">Admins</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-500">
              {team.filter(m => m.role === 'staff').length}
            </p>
            <p className="text-sm text-muted-foreground">Staff</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-500">
              {team.filter(m => m.is_active).length}
            </p>
            <p className="text-sm text-muted-foreground">Actifs</p>
          </CardContent>
        </Card>
      </div>

      {/* Team List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : team.length > 0 ? (
        <div className="space-y-3">
          {team.map((member) => (
            <TeamMemberCard
              key={member.id}
              member={member}
              currentUserId={user?.id}
              isAdmin={isAdmin}
              onEdit={(m) => setEditingMember(m)}
              onDelete={(id) => {
                if (window.confirm('Supprimer ce membre ?')) {
                  deleteMutation.mutate(id);
                }
              }}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">Aucun membre</h3>
            <p className="text-muted-foreground mb-4">
              Invitez votre premier collaborateur
            </p>
            <Button onClick={() => setInviteOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Inviter
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvite={(data) => inviteMutation.mutate(data)}
      />
      
      <EditMemberDialog
        member={editingMember}
        open={!!editingMember}
        onOpenChange={(open) => !open && setEditingMember(null)}
        onSave={(id, data) => updateMutation.mutate({ id, data })}
      />
    </div>
  );
};

export default TeamPage;
